import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import './Login.css';

const Login: React.FC = () => {
  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginMode, setLoginMode] = useState<'dealer' | 'manager'>('dealer');

  const { checkAuth, isAuthenticated, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect already-authenticated users to their dashboard
  useEffect(() => {
    // Only redirect if we're done loading AND user is authenticated
    // Don't block rendering while auth is loading - show login form immediately
    if (!authLoading && isAuthenticated && user) {
      let redirectPath = '/dashboard';
      const userRole = user.role as string;

      if (userRole === 'corporate') redirectPath = '/corporate';
      else if (userRole === 'regional') redirectPath = '/regional';
      else if (userRole === 'district') redirectPath = '/district';
      else if (userRole === 'admin') redirectPath = '/admin';

      navigate(redirectPath, { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let data: any;

      if (loginMode === 'dealer') {
        // Dealer login using dealer ID
        data = await authAPI.dealerLogin(credential, password);
      } else {
        // Manager login using email
        data = await authAPI.login(credential, password);
      }

      if (data.success) {
        console.log('Login successful, redirect URL:', data.redirect);
        console.log('Login response received:', { success: data.success, hasUser: !!data.user, hasToken: !!data.token });

        // Store JWT token for cookie-restricted environments (both manager and dealer login)
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
          console.log('JWT token stored for cookie-restricted environment');
          console.log('Token length:', data.token.length);
          console.log('Stored token verification:', localStorage.getItem('auth_token') ? 'success' : 'failed');
        } else {
          console.warn('No token in login response');
        }

        // Update auth context by checking auth status
        await checkAuth();
        console.log('Auth check completed');
        // Use setTimeout to ensure state is updated before navigation
        setTimeout(() => {
          console.log('Navigating to:', data.redirect || '/dashboard');
          navigate(data.redirect || '/dashboard', { replace: true });
        }, 100);
      } else {
        const errorMsg = loginMode === 'dealer'
          ? (data.error || 'Invalid dealer ID or password')
          : (data.error || 'Invalid email or password');
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-screen-child"></div>
      
      <div className="brand-logos">
        <img 
          className="brand-logo gravely-logo" 
          alt="Gravely Logo" 
          src="/img/gravely_new.svg"
        />
        <img 
          className="brand-logo ariens-logo" 
          alt="Ariens Logo" 
          src="/img/ariens.svg"
        />
      </div>
      
      <form onSubmit={handleSubmit}>
        <b className="sign-in">Sign in</b>
        
        <div className="login-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${loginMode === 'dealer' ? 'active' : ''}`}
            onClick={() => {
              setLoginMode('dealer');
              setCredential('');
              setError('');
            }}
          >
            Dealer Login
          </button>
          <button
            type="button"
            className={`mode-btn ${loginMode === 'manager' ? 'active' : ''}`}
            onClick={() => {
              setLoginMode('manager');
              setCredential('');
              setError('');
            }}
          >
            Manager Login
          </button>
        </div>
        
        <div className="form-group">
          <b className="email">{loginMode === 'dealer' ? 'Dealer ID' : 'Email'}</b>
          <div className="login-screen-item">
            <input
              type={loginMode === 'dealer' ? 'text' : 'email'}
              value={credential}
              onChange={(e) => setCredential(e.target.value)}
              required
              disabled={isLoading}
              placeholder={loginMode === 'dealer' ? 'Enter your dealer ID' : 'Enter your email'}
            />
          </div>
        </div>
        
        <div className="form-group">
          <b className="password">Password</b>
          <div className="login-screen-inner">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your password"
            />
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading}
        >
          <b className="submit">{isLoading ? 'Signing in...' : 'Submit'}</b>
        </button>
      </form>
    </div>
  );
};

export default Login;
